import { useState, type FormEvent } from "react";
import {
  confirmSignUp,
  resendSignUpCode,
  type ConfirmSignUpOutput,
} from "aws-amplify/auth";
import { ErrorMessage } from "./error-message.js";
import { friendlyAuthError } from "./cognito-errors.js";

interface Props {
  username: string;
  /**
   * Called when confirmSignUp returns isSignUpComplete=true. Returns
   * the next step so the parent state machine can decide whether to
   * call autoSignIn (`COMPLETE_AUTO_SIGN_IN`) or fall through to
   * sign-in. Per Amplify v6 issue #13666, autoSignIn is no longer
   * triggered automatically — caller must explicitly check the next
   * step and call autoSignIn() themselves.
   */
  onConfirmed: (result: ConfirmSignUpOutput) => Promise<void> | void;
}

/**
 * 6-digit code entry screen rendered after `signUp`. Cognito sends
 * the code via SES (template-less; the basic Cognito email flow). The
 * user enters the code; we call `confirmSignUp` then bubble the result
 * up so the parent (SignUpPage) can drive the auto-sign-in path.
 */
export function EmailConfirmationForm({ username, onConfirmed }: Props) {
  const [code, setCode] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [resending, setResending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [resentMsg, setResentMsg] = useState<string | null>(null);

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (submitting) return;
    setError(null);
    setSubmitting(true);
    try {
      const result = await confirmSignUp({
        username,
        confirmationCode: code,
      });
      // We don't call autoSignIn() here. Reason: SignUp was not invoked
      // with `options.autoSignIn`, and even when Amplify v6 returns
      // `signUpStep: COMPLETE_AUTO_SIGN_IN` the caller is responsible
      // for picking how to continue. Triggering autoSignIn AND then
      // signIn() in the parent confused Amplify's internal state for
      // Mfa.REQUIRED pools — autoSignIn returns a SignInOutput we
      // dropped on the floor, then signIn() threw
      // UserAlreadyAuthenticatedException against a half-established
      // session that had no MFA completion, leaving the user in a
      // zombie state with no tokens.
      await onConfirmed(result);
    } catch (err) {
      setError(friendlyAuthError(err));
    } finally {
      setSubmitting(false);
    }
  }

  async function onResend() {
    if (resending) return;
    setResending(true);
    setResentMsg(null);
    setError(null);
    try {
      await resendSignUpCode({ username });
      setResentMsg("Code resent. Check your email.");
    } catch (err) {
      setError(friendlyAuthError(err));
    } finally {
      setResending(false);
    }
  }

  return (
    <form onSubmit={(e) => void onSubmit(e)} className="space-y-4">
      <div>
        <h2 className="text-lg font-medium">Confirm your email</h2>
        <p className="mt-1 text-sm text-zinc-600 dark:text-zinc-400">
          We sent a 6-digit code to <span className="font-medium">{username}</span>.
        </p>
      </div>
      <div>
        <label htmlFor="confirm-code" className="sr-only">
          Confirmation code
        </label>
        <input
          id="confirm-code"
          type="text"
          inputMode="numeric"
          autoComplete="one-time-code"
          pattern="[0-9]{6}"
          maxLength={6}
          required
          value={code}
          onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
          autoFocus
          className="w-full rounded-md border border-zinc-300 px-3 py-2 text-center font-mono text-2xl tracking-widest focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 dark:border-zinc-700 dark:bg-zinc-900"
          disabled={submitting}
          placeholder="000000"
        />
      </div>
      {error && <ErrorMessage>{error}</ErrorMessage>}
      {resentMsg && (
        <p
          role="status"
          aria-live="polite"
          className="text-sm text-emerald-700 dark:text-emerald-400"
        >
          {resentMsg}
        </p>
      )}
      <button
        type="submit"
        disabled={submitting || code.length !== 6}
        className="w-full rounded-md bg-brand-500 px-4 py-2.5 text-base font-medium text-white shadow-sm transition hover:bg-brand-600 disabled:cursor-not-allowed disabled:opacity-60"
      >
        {submitting ? "Confirming…" : "Confirm"}
      </button>
      <button
        type="button"
        onClick={() => void onResend()}
        disabled={resending}
        className="w-full text-center text-sm text-brand-600 hover:underline disabled:opacity-60 dark:text-brand-500"
      >
        {resending ? "Resending…" : "Didn't receive the code? Resend"}
      </button>
    </form>
  );
}
